import type { Channel, StreamChat } from 'stream-chat';

/**
 * Pure helpers over Stream channels, shared by a show's Messages tab and the
 * unified Messages inbox. Imports `stream-chat` types only - never
 * stream-chat-react - so it is safe to use from eager code.
 */

/** Page size / cap for the all-channels query (mirrors the mobile client). */
const PAGE = 30;
const MAX_PAGES = 10;
export const CHANNEL_QUERY_CAP = PAGE * MAX_PAGES;

/**
 * Every 'messaging' channel the user is a member of, newest first, up to
 * CHANNEL_QUERY_CAP.
 *
 * Deliberately NOT filtered on the `showId` custom field: both clients have a
 * fast path that watches a bare channel first, which get-or-creates it with no
 * custom data - a DM created that way is invisible to a showId query. The
 * channel id encodes the show (`dm_{showId}_{hash}`, `show-{showId}-all-cast`),
 * so callers scope by id, via parseChannelShow below.
 */
export async function queryAllUserChannels(
  client: StreamChat,
  uid: string,
): Promise<Channel[]> {
  const collected: Channel[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await client.queryChannels(
      {
        type: 'messaging',
        members: { $in: [uid] },
      } as any,
      { last_message_at: -1 },
      { limit: PAGE, offset: page * PAGE },
    );
    collected.push(...response);
    if (response.length < PAGE) break;
  }
  return collected;
}

export interface ChannelShowRef {
  /**
   * `allCast` is the show's own channel; `group` is a group chat someone in
   * the show created. Both are group conversations - see isGroupKind.
   */
  kind: 'dm' | 'allCast' | 'group';
  /**
   * Null for organisation-scoped DMs (`dm_org_{orgId}_{hash}`) - admin
   * conversations with people who are not assigned to any show.
   */
  showId: string | null;
}

// Anchored and unambiguous even if a showId contained '_' or '-': DM ids end
// in exactly 16 hex chars (see utils/chatChannelId.ts), all-cast ids end in
// the literal '-all-cast'. Group chat ids are minted by the createGroupChat
// callable as `grp_{showId}_{16 hex chars}` - never `dm_`, because the mobile
// push handlers treat every id that doesn't start `dm_` as a group.
//
// ORG_DM_ID must be tested before DM_ID - the per-show pattern would happily
// swallow `dm_org_{orgId}_{hash}` and report the show as "org_{orgId}". No
// real show can collide with the org prefix: show ids are Firestore auto-ids,
// which never contain an underscore.
const ORG_DM_ID = /^dm_org_.+_[0-9a-f]{16}$/;
const DM_ID = /^dm_(.+)_[0-9a-f]{16}$/;
const ALL_CAST_ID = /^show-(.+)-all-cast$/;
const GROUP_ID = /^grp_([^_]+)_[0-9a-f]{16}$/;

/** Whether a conversation kind is a group (the show channel or a group chat). */
export function isGroupKind(kind: ChannelShowRef['kind']): boolean {
  return kind !== 'dm';
}

/**
 * Which show a channel belongs to, derived from its id - the one reliable
 * discriminator (see queryAllUserChannels). Null for unknown id shapes, which
 * callers should drop rather than guess at.
 */
export function parseChannelShow(id: string | undefined): ChannelShowRef | null {
  if (!id) return null;
  if (ORG_DM_ID.test(id)) return { kind: 'dm', showId: null };
  const dm = id.match(DM_ID);
  if (dm) return { kind: 'dm', showId: dm[1] };
  const group = id.match(GROUP_ID);
  if (group) return { kind: 'group', showId: group[1] };
  const allCast = id.match(ALL_CAST_ID);
  if (allCast) return { kind: 'allCast', showId: allCast[1] };
  return null;
}

export interface DmOther {
  id: string;
  name: string;
  image?: string;
  role?: string;
  online: boolean;
}

/**
 * The other party in a DM, from the channel's own member state. Stream users
 * carry name/image/userRole (synced server-side by buildStreamUser), so no
 * cast-roster call is needed to render a DM row.
 */
export function getDmOther(channel: Channel, uid: string): DmOther | null {
  const members = Object.values(channel.state?.members || {}) as any[];
  const other = members.find((m) => m.user?.id && m.user.id !== uid);
  if (!other?.user) return null;
  return {
    id: other.user.id,
    name: other.user.name || 'Unknown',
    image: other.user.image as string | undefined,
    role: (other.user as any).userRole as string | undefined,
    online: !!other.user.online,
  };
}

export interface LastMessageMeta {
  text: string;
  authorName: string | null;
  fromYou: boolean;
  at: Date | null;
}

/**
 * The preview line for a channel row. Falls back to `data.last_message_at`
 * when message history isn't cached locally (same trick as the panel's
 * visibleDMs), so a channel still sorts correctly before it is watched.
 */
export function getLastMessageMeta(
  channel: Channel,
  uid: string,
  blocked: ReadonlySet<string> = new Set(),
): LastMessageMeta {
  const messages = channel.state?.messages || [];
  // Skip deleted messages, and anything from someone the reader blocked, for
  // the preview - the thread hides both too.
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message: any = messages[i];
    if (message.type === 'deleted') continue;
    if (message.user?.id && message.user.id !== uid && blocked.has(message.user.id)) continue;
    return {
      text: message.text || (message.attachments?.length ? 'Attachment' : ''),
      authorName: message.user?.name || null,
      fromYou: message.user?.id === uid,
      at: message.created_at ? new Date(message.created_at) : null,
    };
  }
  const lastAt = (channel.data as any)?.last_message_at;
  return {
    text: '',
    authorName: null,
    fromYou: false,
    at: lastAt ? new Date(lastAt) : null,
  };
}
